/**
 * UserStickyPanel Component
 *
 * Individual draggable panel for user-created sticky notes.
 * Features:
 * - Draggable header (desktop only)
 * - Editable title with pencil icon
 * - Editable textarea with autosave
 * - Minimize and delete buttons
 * - Responsive design with mobile support
 */

import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { motion, PanInfo, useReducedMotion, useDragControls, useMotionValue } from 'framer-motion';
import { Minus, X, Pencil, StickyNote, GripHorizontal, Copy, Check } from 'lucide-react';
import type { UserStickyNote, StickyPosition, StickySize } from '@shared/types/userStickies';
import { stickyColorSchemes, dynamicsColorScheme, DEFAULT_STICKY_PLACEHOLDER, MIN_STICKY_SIZE, MAX_STICKY_SIZE } from '@shared/types/userStickies';
import { useIsMobile } from '@shared/hooks/useIsMobile';
import { useUIStore } from '@shared/store/uiStore';
import { logger } from '@shared/utils/logger';

// Debounce time to prevent prop sync from overwriting user typing
const TYPING_DEBOUNCE_MS = 1000;

interface UserStickyPanelProps {
  sticky: UserStickyNote;
  isFocused?: boolean;
  onFocus?: () => void;
  onContentChange: (content: string) => void;
  onTitleChange: (title: string) => void;
  onPositionChange: (position: StickyPosition) => void;
  onSizeChange: (size: StickySize) => void;
  onMinimize: () => void;
  onDelete: () => void;
}

/**
 * Animation variants for panel
 */
const panelVariants = {
  hidden: { opacity: 0, scale: 0.9, y: 10 },
  visible: {
    opacity: 1,
    scale: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 25 },
  },
  exit: { opacity: 0, scale: 0.9, y: 10, transition: { duration: 0.2 } },
};

/**
 * Reduced motion variants
 */
const reducedMotionVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.1 } },
  exit: { opacity: 0, transition: { duration: 0.1 } },
};

export const UserStickyPanel: React.FC<UserStickyPanelProps> = ({
  sticky,
  isFocused = false,
  onFocus,
  onContentChange,
  onTitleChange,
  onPositionChange,
  onSizeChange,
  onMinimize,
  onDelete,
}) => {
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const themeClass = useUIStore((state) => state.themeClass);
  const dragControls = useDragControls();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const titleInputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [localContent, setLocalContent] = useState(sticky.content);
  const [localTitle, setLocalTitle] = useState(sticky.title);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [hasCopied, setHasCopied] = useState(false);

  // Use motion values for position to properly handle drag + position updates
  // This prevents the bug where transform offset accumulates on top of CSS position
  const motionX = useMotionValue(sticky.position.x);
  const motionY = useMotionValue(sticky.position.y);

  // Track when user is actively typing to prevent prop sync race condition
  const lastContentChangeRef = useRef<number>(0);
  const lastTitleChangeRef = useRef<number>(0);

  // Use Dynamics theme color scheme (professional grey) when active, otherwise use sticky color
  const isDynamicsTheme = themeClass === 'theme-dynamics';
  const colorScheme = useMemo(() => {
    return isDynamicsTheme ? dynamicsColorScheme : stickyColorSchemes[sticky.color];
  }, [isDynamicsTheme, sticky.color]);

  // Sync local content with prop - BUT only if user hasn't typed recently
  // This prevents the race condition where prop updates overwrite user typing
  useEffect(() => {
    const timeSinceLastChange = Date.now() - lastContentChangeRef.current;
    if (timeSinceLastChange > TYPING_DEBOUNCE_MS) {
      logger.log(`[UserStickyPanel] Syncing content from prop: "${sticky.content?.slice(0, 20)}..."`);
      setLocalContent(sticky.content);
    } else {
      logger.log(`[UserStickyPanel] BLOCKED prop sync (user typing) - timeSince=${timeSinceLastChange}ms`);
    }
  }, [sticky.content]);

  // Sync local title with prop - BUT only if user hasn't typed recently
  useEffect(() => {
    const timeSinceLastChange = Date.now() - lastTitleChangeRef.current;
    if (timeSinceLastChange > TYPING_DEBOUNCE_MS) {
      logger.log(`[UserStickyPanel] Syncing title from prop: "${sticky.title}"`);
      setLocalTitle(sticky.title);
    } else {
      logger.log(`[UserStickyPanel] BLOCKED title sync (user typing) - timeSince=${timeSinceLastChange}ms`);
    }
  }, [sticky.title]);

  // Sync motion values when position props change (e.g., from persistence)
  // Only sync when NOT dragging to avoid interrupting active drags
  useEffect(() => {
    if (!isDragging) {
      motionX.set(sticky.position.x);
      motionY.set(sticky.position.y);
    }
  }, [sticky.position.x, sticky.position.y, isDragging, motionX, motionY]);

  // Focus title input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Handle keyboard escape to minimize
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (isEditingTitle) {
          setIsEditingTitle(false);
        } else {
          e.preventDefault();
          onMinimize();
        }
      }
    },
    [onMinimize, isEditingTitle]
  );

  // Handle content change with local state
  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newContent = e.target.value;
      lastContentChangeRef.current = Date.now();  // Track typing time
      setLocalContent(newContent);
      onContentChange(newContent);
    },
    [onContentChange]
  );

  // Handle title change
  const handleTitleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newTitle = e.target.value;
      lastTitleChangeRef.current = Date.now();  // Track typing time
      setLocalTitle(newTitle);
    },
    []
  );

  // Handle title blur - save title
  const handleTitleBlur = useCallback(() => {
    setIsEditingTitle(false);
    if (localTitle.trim() !== sticky.title) {
      onTitleChange(localTitle.trim() || 'New Note');
    }
  }, [localTitle, sticky.title, onTitleChange]);

  // Handle title keydown - Enter to save, Escape to cancel
  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleTitleBlur();
      } else if (e.key === 'Escape') {
        setLocalTitle(sticky.title);
        setIsEditingTitle(false);
      }
    },
    [handleTitleBlur, sticky.title]
  );

  // Handle drag start - set dragging state
  const handleDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  // Handle drag end - read final position from motion values
  // Motion values already contain the correct absolute position after drag
  const handleDragEnd = useCallback(
    () => {
      setIsDragging(false);
      const newPosition: StickyPosition = {
        x: Math.max(0, Math.round(motionX.get())),
        y: Math.max(0, Math.round(motionY.get())),
      };
      onPositionChange(newPosition);
    },
    [onPositionChange, motionX, motionY]
  );

  // Start drag from header only
  const startDrag = useCallback(
    (event: React.PointerEvent) => {
      if (!isMobile) {
        dragControls.start(event);
      }
    },
    [dragControls, isMobile]
  );

  // Handle resize start
  const handleResizeStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    if (isMobile) return;
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(true);

    const startX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const startY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    const startWidth = sticky.size?.width ?? 280;
    const startHeight = sticky.size?.height ?? 200;

    const handleMouseMove = (moveEvent: MouseEvent | TouchEvent) => {
      const currentX = 'touches' in moveEvent ? moveEvent.touches[0].clientX : moveEvent.clientX;
      const currentY = 'touches' in moveEvent ? moveEvent.touches[0].clientY : moveEvent.clientY;

      const deltaX = currentX - startX;
      const deltaY = currentY - startY;

      const newWidth = Math.min(MAX_STICKY_SIZE.width, Math.max(MIN_STICKY_SIZE.width, startWidth + deltaX));
      const newHeight = Math.min(MAX_STICKY_SIZE.height, Math.max(MIN_STICKY_SIZE.height, startHeight + deltaY));

      onSizeChange({ width: newWidth, height: newHeight });
    };

    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleMouseMove);
      document.removeEventListener('touchend', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleMouseMove);
    document.addEventListener('touchend', handleMouseUp);
  }, [isMobile, sticky.size, onSizeChange]);

  // Handle copy to clipboard
  const handleCopy = useCallback(async () => {
    try {
      const textToCopy = localContent || '';
      await navigator.clipboard.writeText(textToCopy);
      setHasCopied(true);
      setTimeout(() => setHasCopied(false), 2000);
    } catch (err) {
      logger.error('[UserStickyPanel] Failed to copy to clipboard:', err);
    }
  }, [localContent]);

  const variants = prefersReducedMotion ? reducedMotionVariants : panelVariants;

  // Mobile: fixed position at top center
  // Desktop: draggable with saved position and custom size
  // Focused sticky gets higher z-index to appear on top
  const baseZIndex = 50;
  const focusedZIndex = 100;

  // Panel style - desktop uses motion values for position (not CSS left/top)
  // This allows Framer Motion to properly manage transforms during drag
  const panelStyle = isMobile
    ? {
        position: 'fixed' as const,
        top: '80px',
        left: '16px',
        right: '16px',
        width: 'calc(100% - 32px)',
        maxWidth: '400px',
        margin: '0 auto',
        zIndex: isFocused ? focusedZIndex : baseZIndex,
      }
    : {
        position: 'fixed' as const,
        // Use left: 0, top: 0 as base - actual position controlled by x/y motion values
        left: 0,
        top: 0,
        width: sticky.size?.width ?? 280,
        height: sticky.size?.height ?? 200,
        minWidth: MIN_STICKY_SIZE.width,
        maxWidth: MAX_STICKY_SIZE.width,
        minHeight: MIN_STICKY_SIZE.height,
        // No maxHeight - allow unrestricted vertical resizing
        zIndex: isFocused ? focusedZIndex : baseZIndex,
      };

  return (
    <motion.div
      className={`
        ${colorScheme.bg} ${colorScheme.border}
        border-2 rounded-xl shadow-lg
        overflow-hidden
        flex flex-col
        focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-brand-500
      `}
      style={{
        ...panelStyle,
        // Desktop: use motion values for x/y position (prevents transform accumulation bug)
        x: isMobile ? undefined : motionX,
        y: isMobile ? undefined : motionY,
      }}
      variants={variants}
      initial="hidden"
      animate="visible"
      exit="exit"
      drag={!isMobile}
      dragControls={dragControls}
      dragListener={false}
      dragMomentum={false}
      dragElastic={0}
      dragConstraints={{
        top: 0,
        left: 0,
        right: typeof window !== 'undefined' ? window.innerWidth - (sticky.size?.width ?? 280) : 1000,
        bottom: typeof window !== 'undefined' ? window.innerHeight - (sticky.size?.height ?? 200) : 800,
      }}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onPointerDown={onFocus}
      onKeyDown={handleKeyDown}
      role="complementary"
      aria-label={`Sticky note: ${sticky.title}`}
      aria-expanded={true}
      tabIndex={0}
      data-testid={`user-sticky-panel-${sticky.id}`}
    >
      {/* Header - Drag Handle (desktop) */}
      <div
        onPointerDown={startDrag}
        className={`
          ${colorScheme.header}
          px-3 py-2 flex items-center justify-between
          ${!isMobile ? 'cursor-move' : ''}
          select-none
          touch-none
        `}
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <StickyNote size={18} className={colorScheme.iconColor} aria-hidden="true" />
          {isEditingTitle ? (
            <input
              ref={titleInputRef}
              type="text"
              value={localTitle}
              onChange={handleTitleChange}
              onBlur={handleTitleBlur}
              onKeyDown={handleTitleKeyDown}
              className={`
                flex-1 min-w-0 px-1 py-0.5 text-base md:text-sm font-medium rounded
                ${isDynamicsTheme ? 'bg-dark-surface border-dark-border/30' : 'bg-dark-surface/50/10 border border-dark-border/30'}
                ${colorScheme.headerText}
                focus:outline-none focus:ring-1 focus:ring-brand-400
              `}
              onClick={(e) => e.stopPropagation()}
              aria-label="Edit sticky note title"
              data-testid={`title-input-${sticky.id}`}
            />
          ) : (
            <div className="flex items-center gap-1 flex-1 min-w-0">
              <h3 className={`font-medium text-sm truncate ${colorScheme.headerText}`}>
                {localTitle}
              </h3>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditingTitle(true);
                }}
                className={`
                  p-1 rounded transition-all duration-150
                  ${isDynamicsTheme ? 'hover:bg-dark-surface/40' : 'hover:bg-black/10'}
                  focus:outline-none focus:ring-1 focus:ring-brand-400
                  ${isMobile ? 'min-w-[44px] min-h-[44px] flex items-center justify-center' : ''}
                `}
                aria-label={`Edit title: ${sticky.title}`}
                data-testid={`title-button-${sticky.id}`}
              >
                <Pencil size={12} className={`${colorScheme.iconColor} opacity-60 hover:opacity-100`} aria-hidden="true" />
              </button>
            </div>
          )}
        </div>

        <div className="flex items-center gap-1 ml-2">
          {/* Copy button */}
          <button
            onClick={handleCopy}
            className={`
              p-1 rounded transition-all duration-150
              ${isDynamicsTheme ? 'hover:bg-dark-surface/40' : 'hover:bg-black/10'}
              focus:outline-none focus:ring-2 focus:ring-brand-400
              ${isMobile ? 'min-w-[44px] min-h-[44px] flex items-center justify-center' : ''}
            `}
            aria-label={hasCopied ? 'Copied!' : `Copy ${sticky.title} content`}
            data-testid={`copy-${sticky.id}`}
          >
            {hasCopied ? (
              <Check size={isMobile ? 20 : 16} className="text-green-500" />
            ) : (
              <Copy size={isMobile ? 20 : 16} className={isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'} />
            )}
          </button>

          {/* Minimize button */}
          <button
            onClick={onMinimize}
            className={`
              p-1 rounded transition-all duration-150
              ${isDynamicsTheme ? 'hover:bg-dark-surface/40' : 'hover:bg-black/10'}
              focus:outline-none focus:ring-2 focus:ring-brand-400
              ${isMobile ? 'min-w-[44px] min-h-[44px] flex items-center justify-center' : ''}
            `}
            aria-label={`Minimize ${sticky.title}`}
            data-testid={`minimize-${sticky.id}`}
          >
            <Minus size={isMobile ? 20 : 16} className={isDynamicsTheme ? 'text-dark-muted' : 'text-dark-muted'} />
          </button>

          {/* Delete button */}
          <button
            onClick={onDelete}
            className={`
              p-1 rounded transition-all duration-150
              ${isDynamicsTheme ? 'hover:bg-red-100' : 'hover:bg-red-900/30'}
              focus:outline-none focus:ring-2 focus:ring-red-500
              ${isMobile ? 'min-w-[44px] min-h-[44px] flex items-center justify-center' : ''}
            `}
            aria-label={`Delete ${sticky.title}`}
            data-testid={`delete-${sticky.id}`}
          >
            <X size={isMobile ? 20 : 16} className={isDynamicsTheme ? 'text-red-600' : 'text-red-500'} />
          </button>
        </div>
      </div>

      {/* Body - Textarea (fills remaining space) */}
      <div className="px-3 pb-3 flex-1 flex flex-col min-h-0">
        <textarea
          ref={textareaRef}
          value={localContent}
          onChange={handleContentChange}
          placeholder={DEFAULT_STICKY_PLACEHOLDER}
          className={`
            w-full h-full p-2 rounded flex-1
            bg-transparent border-none
            text-sm
            ${isDynamicsTheme ? 'text-white placeholder-dark-muted' : 'text-white placeholder-dark-muted'}
            resize-none
            focus:outline-none focus:ring-0
            overflow-y-auto
            scrollbar-thin scrollbar-thumb-transparent hover:scrollbar-thumb-gray-400/50
            [&::-webkit-scrollbar]:w-1.5
            [&::-webkit-scrollbar-track]:bg-transparent
            [&::-webkit-scrollbar-thumb]:bg-transparent
            [&::-webkit-scrollbar-thumb]:rounded-full
            hover:[&::-webkit-scrollbar-thumb]:bg-gray-400/50
            focus:[&::-webkit-scrollbar-thumb]:bg-gray-400/50
            ${isMobile ? 'text-base' : ''}
          `}
          onClick={(e) => e.stopPropagation()}
          aria-label={`${sticky.title} content`}
          data-testid={`textarea-${sticky.id}`}
        />
      </div>

      {/* Resize Handle (desktop only) */}
      {!isMobile && (
        <div
          onMouseDown={handleResizeStart}
          onTouchStart={handleResizeStart}
          onPointerDown={(e) => e.stopPropagation()}
          className={`
            absolute bottom-0 right-0 w-6 h-6
            flex items-center justify-center
            cursor-se-resize
            opacity-50 hover:opacity-100 transition-opacity
            ${isResizing ? 'opacity-100' : ''}
          `}
          aria-label="Resize sticky note"
          role="slider"
          aria-valuemin={MIN_STICKY_SIZE.width}
          aria-valuemax={MAX_STICKY_SIZE.width}
          aria-valuenow={sticky.size?.width ?? 280}
        >
          <GripHorizontal
            size={14}
            className={`${colorScheme.iconColor} rotate-[-45deg]`}
          />
        </div>
      )}
    </motion.div>
  );
};
